import * as THREE from 'three';

import type { StaffSpot } from '../config/booth';
import type { Agent } from '../sim/types';
import { BRAND } from '../textures/canvas';

/** Attendee shirt colours: muted show-floor palette (lots of tan, olive, black, denim). */
const SHIRTS = [
  '#4b4f58',
  '#6f6a5a',
  '#56653f',
  '#2d3a55',
  '#8a8378',
  '#3b3b3b',
  '#7a4a3a',
  '#5e6a7a',
  '#a08c6a',
  '#334a3a',
];

const skin = new THREE.MeshStandardMaterial({
  color: '#d9b08c',
  roughness: 0.8,
});
const trousers = new THREE.MeshStandardMaterial({
  color: '#2a2d35',
  roughness: 0.9,
});
/** Staff wear navy Project Parabellum polos; the logo print is a decal on top. */
const staffShirt = new THREE.MeshStandardMaterial({
  color: BRAND.navy,
  roughness: 0.7,
});
const lanyard = new THREE.MeshStandardMaterial({
  color: BRAND.red,
  roughness: 0.7,
});
const shirtMats = SHIRTS.map(
  (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.85 }),
);
const reachMat = new THREE.MeshBasicMaterial({
  color: BRAND.red,
  transparent: true,
  opacity: 0.16,
  depthWrite: false,
  side: THREE.DoubleSide,
});
const reachEdge = new THREE.MeshBasicMaterial({
  color: BRAND.red,
  transparent: true,
  opacity: 0.5,
  depthWrite: false,
  side: THREE.DoubleSide,
});

const bodyGeo = new THREE.CapsuleGeometry(0.46, 1.5, 4, 10);
const legGeo = new THREE.CylinderGeometry(0.22, 0.19, 2.4, 8);
const headGeo = new THREE.SphereGeometry(0.36, 12, 10);
const badgeGeo = new THREE.BoxGeometry(0.4, 0.5, 0.04);

/** ~5'8" person: legs to 2.4', torso capsule, head topping out near 5.9'. */
function figure(
  shirt: THREE.Material,
  print: THREE.Texture | null,
): THREE.Group {
  const g = new THREE.Group();
  const legs = new THREE.Mesh(legGeo, trousers);
  legs.position.y = 1.2;
  legs.castShadow = true;
  g.add(legs);
  const body = new THREE.Mesh(bodyGeo, shirt);
  body.position.y = 3.6;
  body.castShadow = true;
  g.add(body);
  const head = new THREE.Mesh(headGeo, skin);
  head.position.y = 5.25;
  head.castShadow = true;
  g.add(head);
  if (print) {
    const printMat = new THREE.MeshStandardMaterial({
      map: print,
      transparent: true,
      roughness: 0.8,
    });
    // Chest print, small and left of centre like an embroidered polo.
    const chest = new THREE.Mesh(
      new THREE.PlaneGeometry(0.42, 0.42 * (160 / 1024)),
      printMat,
    );
    chest.position.set(-0.14, 4.1, 0.455);
    g.add(chest);
    // Back print, large, for the aisle behind.
    const back = new THREE.Mesh(
      new THREE.PlaneGeometry(0.72, 0.72 * (160 / 1024)),
      printMat,
    );
    back.position.set(0, 4.0, -0.455);
    back.rotation.y = Math.PI;
    g.add(back);
    const badge = new THREE.Mesh(badgeGeo, lanyard);
    badge.position.set(0.16, 3.5, 0.46);
    g.add(badge);
  }
  return g;
}

export interface Crowd {
  group: THREE.Group;
  sync(agents: ReadonlyArray<Agent>, dt: number): void;
}

/** Keeps one mesh per live agent and walks them in place a little. */
export function buildCrowd(): Crowd {
  const group = new THREE.Group();
  const live = new Map<number, THREE.Group>();
  let t = 0;
  return {
    group,
    sync(agents, dt) {
      t += dt;
      const seen = new Set<number>();
      for (const a of agents) {
        seen.add(a.id);
        let g = live.get(a.id);
        if (!g) {
          g = figure(shirtMats[Math.floor(a.tint * shirtMats.length)]!, null);
          live.set(a.id, g);
          group.add(g);
        }
        g.position.set(a.x, 0, a.z);
        g.rotation.y = a.heading;
        const walking =
          a.state === 'walking' ||
          a.state === 'leaving' ||
          (a.state === 'queued' && a.stuck === 0);
        g.position.y = walking ? Math.abs(Math.sin(t * 9 + a.id)) * 0.06 : 0;
      }
      for (const [id, g] of live) {
        if (seen.has(id)) continue;
        group.remove(g);
        live.delete(id);
      }
    },
  };
}

export interface StaffSet {
  group: THREE.Group;
  roots: Map<string, THREE.Group>;
  place(s: StaffSpot): void;
  setReachVisible(on: boolean): void;
}

export function buildStaff(
  staff: StaffSpot[],
  reach: number,
  print: THREE.Texture,
): StaffSet {
  const group = new THREE.Group();
  const roots = new Map<string, THREE.Group>();
  const rings: THREE.Object3D[] = [];
  for (const s of staff) {
    const g = figure(staffShirt, print);
    g.userData['id'] = s.id;
    g.userData['kind'] = 'staff';
    g.position.set(s.x, 0, s.z);
    g.rotation.y = Math.PI; // Face the main aisle by default.
    const disc = new THREE.Mesh(new THREE.CircleGeometry(reach, 40), reachMat);
    disc.rotation.x = -Math.PI / 2;
    disc.position.y = 0.03;
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(reach - 0.08, reach, 48),
      reachEdge,
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.035;
    g.add(disc, ring);
    rings.push(disc, ring);
    roots.set(s.id, g);
    group.add(g);
  }
  return {
    group,
    roots,
    place(s) {
      roots.get(s.id)?.position.set(s.x, 0, s.z);
    },
    setReachVisible(on) {
      for (const r of rings) r.visible = on;
    },
  };
}
